import { ContainerData, ModuleClickAction, ModuleData } from '../Types/containerV2';

export type SelectionAction = Extract<ModuleClickAction, { type: 'select-value' }>;
const groupPattern = /^[A-Za-z][A-Za-z0-9_]*$/;

export function collectSelectionOptions(container?: ContainerData): SelectionAction[] {
	const options: SelectionAction[] = [];
	const visit = (modules: ModuleData[]) => {
		for (const module of modules) {
			if (module.moduleType === 'Button' && module.onClick?.type === 'select-value') options.push(module.onClick);
			visit(module.children);
		}
	};
	container?.placeholders.forEach((placeholder) => visit(placeholder.modules));
	return options;
}

/** Parse only query values. Rebuild only values containing placeholders, preserving other URL parts. */
function parseSelectionUrl(href: string) {
	if (!href.trim() || /^(?!https?:)[a-z][a-z0-9+.-]*:/i.test(href.trim())) throw new Error('Use an HTTP URL or page path.');
	const hashIndex = href.indexOf('#');
	const base = hashIndex < 0 ? href : href.slice(0, hashIndex);
	const hash = hashIndex < 0 ? '' : href.slice(hashIndex);
	const queryIndex = base.indexOf('?');
	const path = queryIndex < 0 ? base : base.slice(0, queryIndex);
	const decode = (text: string) => decodeURIComponent(text.replace(/\+/g, ' '));
	const rejectBraces = (text: string) => {
		if (/[{}]/.test(decode(text))) throw new Error('Placeholders are only allowed in query parameter values.');
	};
	rejectBraces(path);
	rejectBraces(hash);
	const groups = new Set<string>();
	const parts = queryIndex < 0 ? [] : base.slice(queryIndex + 1).split('&');
	const values = parts.map((part) => {
		const equals = part.indexOf('=');
		rejectBraces(equals < 0 ? part : part.slice(0, equals));
		const value = equals < 0 ? '' : decode(part.slice(equals + 1));
		const remaining = value.replace(/\{([A-Za-z][A-Za-z0-9_]*)\}/g, (_, group: string) => {
			groups.add(group);
			return '';
		});
		if (/[{}]/.test(remaining)) throw new Error('Use placeholders such as {amount}.');
		return { part, equals, value };
	});
	return {
		groups: [...groups],
		resolve: (selected: ReadonlyMap<string, string>) => {
			if ([...groups].some((group) => !selected.has(group))) return undefined;
			const query = values
				.map(({ part, equals, value }) => {
					if (!value.includes('{')) return part;
					return (
						part.slice(0, equals + 1) +
						encodeURIComponent(
							value.replace(/\{([A-Za-z][A-Za-z0-9_]*)\}/g, (_, group: string) => selected.get(group)!)
						)
					);
				})
				.join('&');
			return path + (queryIndex < 0 ? '' : '?' + query) + hash;
		},
	};
}

export function getSelectionErrors(action: ModuleClickAction | undefined, options: SelectionAction[]): string[] {
	if (action?.type === 'select-value') {
		const errors: string[] = [];
		if (!groupPattern.test(action.group))
			errors.push('Group name must start with a letter and contain only letters, digits or underscores.');
		if (!action.value.trim()) errors.push('Enter a non-empty value.');
		if (options.filter((option) => option.group === action.group && option.value === action.value).length > 1)
			errors.push('Each button in a group must have a unique value.');
		return errors;
	}
	if (action?.type !== 'link' || !action.useSelectedValues) return [];
	if (action.link.type !== 'url') return ['Selected values can only be used with URL links.'];
	try {
		const parsed = parseSelectionUrl(action.link.href ?? '');
		return parsed.groups.flatMap((group) => {
			const members = options.filter((option) => option.group === group);
			if (!members.length) return [`Unknown selection group: ${group}.`];
			return members.some((member) => getSelectionErrors(member, options).length > 0)
				? [`Fix the configuration of selection group: ${group}.`]
				: [];
		});
	} catch (error) {
		return [error instanceof URIError ? 'The URL contains invalid encoding.' : (error as Error).message];
	}
}

export function resolveSelectionLink(
	action: Extract<ModuleClickAction, { type: 'link' }>,
	selected: ReadonlyMap<string, string>
) {
	if (!action.useSelectedValues) return action.link;
	if (action.link.type !== 'url') return undefined;
	try {
		const href = parseSelectionUrl(action.link.href ?? '').resolve(selected);
		return href === undefined ? undefined : { ...action.link, href };
	} catch {
		return undefined;
	}
}
